/* ============================================================
   FM — shared "Add free sample to cart" click handler
   ============================================================
   Loaded once (deferred) on any page that renders a
   `.js-fm-sample-add` button, via snippets/fm-sample-handler.liquid.
   Idempotency-guarded so multiple identical <script> tags (one per
   placement) only bind one listener. Delegated on the document so it
   also catches buttons injected after load (e.g. the options modal).

   Button contract (data-* attributes on `.js-fm-sample-add`):
     data-sample-variant   REQUIRED. Variant id of the shared Free Sample
                           product (resolve via all_products['free-sample']).
     data-product-title    REQUIRED. Real product title -> `Product` prop.
     data-product-handle   REQUIRED. Real product handle (dedupe key).
     data-product-url      Optional. Real product URL.
     data-default-variant  Real product's default/selected variant id.
     data-live             Presence = this button represents the CURRENT
                           PDP product, so track the live colour/size from
                           `.custom-atc-basket[data-variant_id]`. OMIT on
                           card buttons that sample a DIFFERENT product
                           (recommendations, case studies) — those use
                           their own data-default-variant, never the PDP ATC.
     data-variant-colour   Optional. Pre-resolved COLOUR option value (card mode).
     data-variant-sku      Optional. Pre-resolved variant SKU (card mode).

   Colour/SKU resolution order: window.fmSampleVariants[handle][id]
   (emitted by snippets/pdp-sample-request.liquid for the live PDP product,
   shape { colour, sku }) -> then the button's data-variant-colour /
   data-variant-sku attributes. Only the COLOUR is captured (not size) so
   the cart shows "Colour: Charcoal", never a misleading "60cm x 90cm".
   ============================================================ */
(function () {
  if (window.__fmSampleAddBound) return;
  window.__fmSampleAddBound = true;

  var MAX_SAMPLES = 3;

  /* ---- Marketing consent (transparent value exchange for the free sample) ----
     The customer must actively tick an UNticked box before any sample is added
     (see the modal below). Consent is recorded once per session and stamped onto
     every sample line as hidden properties (_marketing_consent / _consent_channels
     / _consent_text / _consent_ts) so the order carries an auditable record for
     the Power Automate -> Klaviyo subscribe step — independent of the checkout
     marketing checkbox and the cookie banner. EDIT CONSENT_TEXT here if the
     wording changes. */
  /* Plain-text version stored on the order / Klaviyo profile (audit record). */
  var CONSENT_TEXT = 'To receive your free sample, you must accept our Terms and Conditions (https://www.firstmats.co.uk/pages/terms)';
  /* Displayed version (HTML) with the Terms and Conditions link. Static string,
     so innerHTML is safe. Keep CONSENT_TEXT and CONSENT_HTML wording in sync. */
  var CONSENT_HTML = 'To receive your free sample, you must accept our <a href="https://www.firstmats.co.uk/pages/terms" target="_blank" rel="noopener">Terms and Conditions</a>';
  var CONSENT_CHANNELS = 'email,whatsapp';
  var CONSENT_KEY = 'fmSampleConsent';

  function getConsent() {
    try { return JSON.parse(sessionStorage.getItem(CONSENT_KEY) || 'null'); } catch (e) { return null; }
  }
  function setConsent() {
    var c = { text: CONSENT_TEXT, channels: CONSENT_CHANNELS, ts: new Date().toISOString() };
    try { sessionStorage.setItem(CONSENT_KEY, JSON.stringify(c)); } catch (e) {}
    return c;
  }

  /* Show feedback near the button. Prefers a dedicated styled element
     (the PDP inline button provides `.pdp-sample-request__msg`); else
     injects a lightweight message right after the button so card/off-PDP
     buttons get feedback too. */
  function showMsg(btn, text, warn) {
    var wrap = btn.closest('.pdp-sample-request');
    var dedicated = wrap && wrap.querySelector('.pdp-sample-request__msg');
    if (dedicated) {
      dedicated.textContent = text;
      dedicated.hidden = false;
      dedicated.classList.toggle('pdp-sample-request__msg--warn', !!warn);
      return;
    }
    var el = btn.parentNode && btn.parentNode.querySelector('.fm-sample-msg');
    if (!el) {
      el = document.createElement('span');
      el.className = 'fm-sample-msg';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.style.cssText = 'display:block;width:100%;margin-top:6px;font-size:1.3rem;line-height:1.35;';
      btn.insertAdjacentElement('afterend', el);
    }
    el.textContent = text;
    el.style.color = warn ? '#b4541b' : 'inherit';
  }

  function resolveRealVariant(btn) {
    /* Only PDP-product buttons (data-live) follow the live colour/size
       selection; card buttons sample their own product's default. */
    if (btn.hasAttribute('data-live')) {
      var atc = document.querySelector('.custom-atc-basket[data-variant_id]');
      var liveId = atc && atc.getAttribute('data-variant_id');
      if (liveId && String(liveId).trim()) return String(liveId).trim();
    }
    return btn.getAttribute('data-default-variant') || '';
  }

  function variantMeta(btn, handle, variantId) {
    var map = window.fmSampleVariants && window.fmSampleVariants[handle];
    if (map && map[variantId]) return map[variantId];   /* { colour, sku, image } */
    return {
      colour: btn.getAttribute('data-variant-colour') || '',
      sku: btn.getAttribute('data-variant-sku') || '',
      image: btn.getAttribute('data-variant-image') || ''
    };
  }

  /* One-time scoped stylesheet used by the open() SAFETY NET below.
     It forces the drawer fully open, but ONLY while BOTH `.fm-forced` AND
     `.active` are present — so when the drawer closes (CartDrawer.close()
     removes `active`) these rules stop applying and the panel hides/animates
     out normally. No lingering inline styles, no manual cleanup. */
  function injectForceStyle() {
    if (document.getElementById('fm-sample-drawer-force')) return;
    var st = document.createElement('style');
    st.id = 'fm-sample-drawer-force';
    st.textContent =
      'cart-drawer.fm-forced.active{visibility:visible !important;transition:none !important;}' +
      'cart-drawer.fm-forced.active .drawer__inner{transform:translateX(0) !important;transition:none !important;}';
    document.head.appendChild(st);
  }

  /* Refresh + open the cart drawer.
     ------------------------------------------------------------------
     Mirrors the theme's own add-to-cart open (assets/global.js
     `addToCartAndOpenDrawer`): re-fetch the cart-drawer section, REPLACE
     the whole <cart-drawer> element with a fresh (closed) one, then add
     `animate active` to slide it in. We drive it ourselves (rather than
     calling that helper) so we control the timing for the safety net.

     We do NOT use cart-drawer.renderContents(): it only swaps
     #CartDrawer.innerHTML and the open transition then never fires —
     the panel stays parked off-screen at translateX(100%) (the
     "drawer appears empty" bug).

     SAFETY NET: this theme's slide-in transition is intermittently flaky
     (the theme author's own code notes it "doesn't always get triggered").
     If the panel is still off-screen shortly after opening, we add
     `.fm-forced` so the scoped rules above snap it open. */
  function openCartDrawer() {
    injectForceStyle();
    fetch('/cart?section_id=cart-drawer')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
        var existing = document.querySelector('cart-drawer');
        if (!fresh || !existing) return;
        var count = Number(fresh.getAttribute('data-cart-items')) || 0;
        existing.replaceWith(fresh);
        void fresh.offsetWidth;                       /* paint the closed state */
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { fresh.classList.add('animate', 'active'); });
        });
        setTimeout(function () {
          if (typeof window.reRenderReviewsIoRatingSnippet === 'function') window.reRenderReviewsIoRatingSnippet();
          if (typeof window.cartExtraJS === 'function') window.cartExtraJS();
          /* keep the header cart-count bubble in sync (mirrors the theme helper) */
          var span = document.querySelector('.eq-header__action .cart-count-bubble span');
          if (span) {
            span.textContent = count;
          } else if (count > 0) {
            var icon = document.querySelector('#cart-icon-bubble');
            if (icon && !icon.querySelector('.cart-count-bubble')) {
              icon.insertAdjacentHTML('beforeend', '<div class="cart-count-bubble"><span aria-hidden="true">' + count + '</span></div>');
            }
          }
        }, 120);
        setTimeout(function () {
          var o = document.querySelector('cart-drawer');
          if (!o) return;
          var inner = o.querySelector('.drawer__inner');
          if (!inner) return;
          o.classList.add('animate', 'active');
          if (inner.getBoundingClientRect().x >= window.innerWidth - 20) {
            o.classList.add('fm-forced');     /* slide-in flaked — snap it open */
          }
        }, 420);
      })
      .catch(function (e) { console.error('FM sample drawer open failed', e); });
  }

  function performAdd(btn) {
    var sampleVariantId = parseInt(btn.getAttribute('data-sample-variant'), 10);
    if (!sampleVariantId) { showMsg(btn, 'Samples are unavailable right now.', true); return; }

    var productTitle = btn.getAttribute('data-product-title') || '';
    var handle       = btn.getAttribute('data-product-handle') || '';
    var productUrl   = btn.getAttribute('data-product-url') || '';
    var realVariant  = resolveRealVariant(btn);
    var meta         = variantMeta(btn, handle, realVariant);

    btn.disabled = true;
    var labelEl = btn.querySelector('.pdp-sample-request__label');
    var originalLabel = labelEl ? labelEl.textContent : null;
    if (labelEl) labelEl.textContent = 'Adding...';

    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var sampleQty = 0;
        var dup = false;
        (cart.items || []).forEach(function (item) {
          if (item.variant_id !== sampleVariantId) return;
          sampleQty += item.quantity;
          var p = item.properties || {};
          if (p._product_handle === handle &&
              (!realVariant || String(p._variant_id) === String(realVariant))) {
            dup = true;
          }
        });

        if (dup) {
          showMsg(btn, 'That sample is already in your cart.', false);
          return Promise.reject('dup');
        }
        if (sampleQty >= MAX_SAMPLES) {
          showMsg(btn, 'You can add up to ' + MAX_SAMPLES + ' free samples. Remove one to add another.', true);
          return Promise.reject('max');
        }

        var props = {
          'Product': productTitle,
          '_sample': 'true',
          '_product_handle': handle,
          '_product_url': productUrl
        };
        if (realVariant) props['_variant_id'] = realVariant;
        /* Only the COLOUR the customer wants is meaningful for a sample.
           We deliberately do NOT use the full variant title (e.g.
           "Charcoal / 60cm x 90cm") — the size would misleadingly read as
           the sample's dimensions. meta.colour is the colour option value
           only (resolved in Liquid); blank when the product has no colour
           option, in which case no Colour line is shown. */
        if (meta.colour) props['Colour'] = meta.colour;
        /* Hidden: the chosen variant's image URL, used by the cart drawer to
           show the actual (e.g. red) mat the customer is sampling instead of
           the generic Free Sample product image. Underscore keeps it out of
           the visible property list. */
        if (meta.image) props['_image'] = meta.image;
        /* Leading underscore = hidden from the cart, checkout, order
           confirmation and customer emails, but STILL stored and visible
           in the Shopify admin order + available to Flow/exports. Keeps the
           SKU as a warehouse pick reference without showing it publicly. */
        if (meta.sku) props['_sku'] = meta.sku;
        /* Marketing-consent record (hidden) — set from the session consent the
           customer actively gave in the modal before any sample was added.
           Carried into the order for the Power Automate -> Klaviyo subscribe. */
        var consent = getConsent();
        if (consent) {
          props['_marketing_consent'] = 'true';
          props['_consent_channels'] = consent.channels;
          props['_consent_text'] = consent.text;
          props['_consent_ts'] = consent.ts;
        }

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: sampleVariantId, quantity: 1, properties: props })
        }).then(function (r) { return r.json(); }).then(function () {
          openCartDrawer();
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'add_sample_to_cart',
            order_type: 'sample',
            ecommerce: {
              currency: 'GBP',
              items: [{
                item_id: meta.sku || handle,
                item_name: productTitle,
                item_variant: meta.colour || undefined,
                item_category: 'Sample',
                price: 0,
                quantity: 1
              }]
            }
          });
          showMsg(btn, 'Sample added to your cart.', false);
        });
      })
      .catch(function (err) {
        if (err !== 'dup' && err !== 'max') {
          console.error('FM sample add failed', err);
          showMsg(btn, 'Sorry, something went wrong adding your sample.', true);
        }
      })
      .then(function () {
        btn.disabled = false;
        if (labelEl && originalLabel !== null) labelEl.textContent = originalLabel;
      });
  }

  /* ---- Consent modal ----
     Shown the first time a sample is added in a session. Unticked checkbox;
     the "Add free sample" button stays disabled until the box is ticked.
     On confirm we record consent (session) and proceed with the add; if the
     customer cancels, nothing is added. Built once, reused. */
  var consentPendingBtn = null;

  function injectConsentStyle() {
    if (document.getElementById('fm-consent-style')) return;
    var st = document.createElement('style');
    st.id = 'fm-consent-style';
    st.textContent = [
      '#fm-consent-overlay{position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:1000000;display:none;align-items:center;justify-content:center;padding:16px;}',
      '#fm-consent-overlay[data-open]{display:flex;}',
      '#fm-consent-modal{background:#fff;border-radius:12px;max-width:440px;width:100%;padding:24px;box-shadow:0 8px 48px rgba(0,0,0,.22);font-family:var(--fm-font-body);box-sizing:border-box;}',
      '#fm-consent-modal h2{margin:0 0 6px;font-size:1.9rem;font-weight:700;color:#1f2433;}',
      '.fm-consent__sub{margin:0 0 16px;font-size:1.45rem;color:#5b6170;line-height:1.4;}',
      '#fm-consent-modal .fm-consent__check{display:flex;gap:14px;align-items:flex-start;font-size:1.4rem;line-height:1.5;color:#1f2433;cursor:pointer;background:#f7f8fb;border:1px solid #e7e9f0;border-radius:8px;padding:14px 16px !important;margin:0 !important;}',
      '#fm-consent-modal .fm-consent__check input{margin:3px 0 0 !important;width:18px;height:18px;flex:0 0 18px;cursor:pointer;}',
      '#fm-consent-modal .fm-consent__check a{color:var(--fm-color-primary,#398c61);text-decoration:underline;}',
      '#fm-consent-modal .fm-consent__check > span{flex:1;}',
      '.fm-consent__actions{display:flex;gap:10px;margin-top:20px;}',
      '.fm-consent__btn{flex:1;height:50px;border-radius:var(--fm-radius-btn,8px);font-family:inherit;font-size:1.5rem;font-weight:600;cursor:pointer;border:1.5px solid var(--fm-color-primary,#398c61);transition:opacity .15s ease;}',
      '.fm-consent__btn--add{background:var(--fm-color-primary,#398c61);color:#fff;}',
      '.fm-consent__btn--add:disabled{opacity:.5;cursor:default;}',
      '.fm-consent__btn--cancel{background:transparent;color:var(--fm-color-primary,#398c61);}'
    ].join('');
    document.head.appendChild(st);
  }

  function buildConsentModal() {
    var overlay = document.getElementById('fm-consent-overlay');
    if (overlay) return overlay;
    injectConsentStyle();
    overlay = document.createElement('div');
    overlay.id = 'fm-consent-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'fm-consent-title');
    overlay.innerHTML =
      '<div id="fm-consent-modal">' +
        '<h2 id="fm-consent-title">Almost there</h2>' +
        '<p class="fm-consent__sub" id="fm-consent-sub">Your free sample is on us.</p>' +
        '<label class="fm-consent__check">' +
          '<input type="checkbox" id="fm-consent-checkbox">' +
          '<span></span>' +
        '</label>' +
        '<div class="fm-consent__actions">' +
          '<button type="button" class="fm-consent__btn fm-consent__btn--cancel" id="fm-consent-cancel">Cancel</button>' +
          '<button type="button" class="fm-consent__btn fm-consent__btn--add" id="fm-consent-add" disabled>Add free sample</button>' +
        '</div>' +
      '</div>';
    /* textContent (not innerHTML) for the wording — avoids any markup issues */
    overlay.querySelector('.fm-consent__check span').innerHTML = CONSENT_HTML;
    document.body.appendChild(overlay);

    var cb = overlay.querySelector('#fm-consent-checkbox');
    var addBtn = overlay.querySelector('#fm-consent-add');
    var cancelBtn = overlay.querySelector('#fm-consent-cancel');

    function close() {
      overlay.removeAttribute('data-open');
      cb.checked = false;
      addBtn.disabled = true;
      consentPendingBtn = null;
    }
    cb.addEventListener('change', function () { addBtn.disabled = !cb.checked; });
    cancelBtn.addEventListener('click', close);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    document.addEventListener('keyup', function (e) { if (e.key === 'Escape' && overlay.hasAttribute('data-open')) close(); });
    addBtn.addEventListener('click', function () {
      if (!cb.checked) return;
      setConsent();
      var b = consentPendingBtn;
      close();
      if (b) performAdd(b);
    });
    return overlay;
  }

  function openConsentModal(btn) {
    var overlay = buildConsentModal();
    consentPendingBtn = btn;
    var title = btn.getAttribute('data-product-title') || '';
    var sub = overlay.querySelector('#fm-consent-sub');
    if (sub) sub.textContent = title ? ('Your free sample of ' + title + ' is on us.') : 'Your free sample is on us.';
    var cb = overlay.querySelector('#fm-consent-checkbox');
    if (cb) cb.checked = false;
    overlay.querySelector('#fm-consent-add').disabled = true;
    overlay.setAttribute('data-open', '');
    if (cb) cb.focus();
  }

  /* ============================================================
     MULTI-COLOUR CHOOSER (products with a custom.sample_product sample set)
     ============================================================
     window.fmSampleSets[handle] = { handle, title, productTitle, productUrl,
       variants: [{ id, colour, sku, available, image }] }   (emitted by
     snippets/pdp-sample-request.liquid). Opens a popup to multi-select colours
     with consent built in, then adds the chosen REAL sample SKUs in one call. */

  function injectChooseStyle() {
    if (document.getElementById('fm-choose-style')) return;
    var st = document.createElement('style');
    st.id = 'fm-choose-style';
    st.textContent = [
      '#fm-choose-overlay{position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:1000000;display:none;align-items:center;justify-content:center;padding:16px;}',
      '#fm-choose-overlay[data-open]{display:flex;}',
      '#fm-choose-modal{background:#fff;border-radius:12px;max-width:520px;width:100%;padding:24px;box-shadow:0 8px 48px rgba(0,0,0,.22);font-family:var(--fm-font-body);box-sizing:border-box;position:relative;max-height:90vh;overflow:auto;}',
      '#fm-choose-close{position:absolute;top:10px;right:12px;background:none;border:none;font-size:22px;line-height:1;cursor:pointer;color:#444;}',
      '#fm-choose-modal h2{margin:0 4px 4px 0;font-size:1.9rem;font-weight:700;color:#1f2433;}',
      '#fm-choose-modal .fm-choose__sub{margin:0 0 16px;font-size:1.45rem;color:#5b6170;line-height:1.4;}',
      '#fm-choose-modal .fm-choose__grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(104px,1fr));gap:10px;margin:0 0 18px;}',
      '#fm-choose-modal .fm-choose__tile{display:flex;flex-direction:column;align-items:center;gap:6px;padding:10px;border:2px solid #e7e9f0;border-radius:10px;background:#fff;cursor:pointer;font-family:inherit;}',
      '#fm-choose-modal .fm-choose__tile img,#fm-choose-modal .fm-choose__swatch{width:56px;height:56px;border-radius:8px;object-fit:cover;background:#f0f1f5;display:block;}',
      '#fm-choose-modal .fm-choose__name{font-size:1.35rem;color:#1f2433;font-weight:600;text-align:center;line-height:1.2;}',
      '#fm-choose-modal .fm-choose__state{font-size:1.1rem;color:#8a90a0;min-height:13px;}',
      '#fm-choose-modal .fm-choose__tile.is-selected{border-color:var(--fm-color-primary,#398c61);box-shadow:0 0 0 1px var(--fm-color-primary,#398c61);}',
      '#fm-choose-modal .fm-choose__tile.is-added{opacity:.5;cursor:default;}',
      '#fm-choose-modal .fm-choose__consent{display:flex;gap:14px;align-items:flex-start;font-size:1.4rem;line-height:1.5;color:#1f2433;cursor:pointer;background:#f7f8fb;border:1px solid #e7e9f0;border-radius:8px;padding:14px 16px !important;margin:0 0 16px !important;}',
      '#fm-choose-modal .fm-choose__consent input{margin:3px 0 0 !important;width:18px;height:18px;flex:0 0 18px;cursor:pointer;}',
      '#fm-choose-modal .fm-choose__consent > span{flex:1;}',
      '#fm-choose-modal .fm-choose__consent a{color:var(--fm-color-primary,#398c61);text-decoration:underline;}',
      '#fm-choose-modal .fm-choose__actions{display:flex;gap:10px;}',
      '#fm-choose-modal .fm-choose__btn{flex:1;height:50px;border-radius:var(--fm-radius-btn,8px);font-family:inherit;font-size:1.5rem;font-weight:600;cursor:pointer;border:1.5px solid var(--fm-color-primary,#398c61);}',
      '#fm-choose-modal .fm-choose__btn--add{background:var(--fm-color-primary,#398c61);color:#fff;}',
      '#fm-choose-modal .fm-choose__btn--add:disabled{opacity:.5;cursor:default;}',
      '#fm-choose-modal .fm-choose__btn--cancel{background:transparent;color:var(--fm-color-primary,#398c61);}'
    ].join('');
    document.head.appendChild(st);
  }

  function buildChooseModal() {
    var overlay = document.getElementById('fm-choose-overlay');
    if (overlay) return overlay;
    injectChooseStyle();
    overlay = document.createElement('div');
    overlay.id = 'fm-choose-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'fm-choose-title');
    overlay.innerHTML =
      '<div id="fm-choose-modal">' +
        '<button type="button" id="fm-choose-close" aria-label="Close">✕</button>' +
        '<h2 id="fm-choose-title">Choose your free samples</h2>' +
        '<p class="fm-choose__sub" id="fm-choose-sub"></p>' +
        '<div class="fm-choose__grid" id="fm-choose-grid"></div>' +
        '<label class="fm-choose__consent"><input type="checkbox" id="fm-choose-consent"><span></span></label>' +
        '<div class="fm-choose__actions">' +
          '<button type="button" class="fm-choose__btn fm-choose__btn--cancel" id="fm-choose-cancel">Cancel</button>' +
          '<button type="button" class="fm-choose__btn fm-choose__btn--add" id="fm-choose-add" disabled><span>Add my samples</span></button>' +
        '</div>' +
      '</div>';
    overlay.querySelector('.fm-choose__consent span').innerHTML = CONSENT_HTML;
    document.body.appendChild(overlay);
    function close() { overlay.removeAttribute('data-open'); }
    overlay.querySelector('#fm-choose-close').addEventListener('click', close);
    overlay.querySelector('#fm-choose-cancel').addEventListener('click', close);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    document.addEventListener('keyup', function (e) { if (e.key === 'Escape' && overlay.hasAttribute('data-open')) close(); });
    return overlay;
  }

  function chooseAdd(set, ids, done) {
    var consent = setConsent();
    var items = ids.map(function (vid) {
      var v = set.variants.filter(function (x) { return String(x.id) === String(vid); })[0] || {};
      var props = {
        'Product': set.productTitle || '',
        '_sample': 'true',
        '_product_url': set.productUrl || '',
        '_marketing_consent': 'true',
        '_consent_channels': consent.channels,
        '_consent_text': consent.text,
        '_consent_ts': consent.ts
      };
      if (v.colour) props['Colour'] = v.colour;
      return { id: parseInt(vid, 10), quantity: 1, properties: props };
    });
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) { return r.json(); }).then(function () {
      openCartDrawer();
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'add_sample_to_cart',
        order_type: 'sample',
        ecommerce: {
          currency: 'GBP',
          items: items.map(function (it) {
            return { item_id: it.id, item_name: set.productTitle, item_variant: it.properties.Colour, item_category: 'Sample', price: 0, quantity: 1 };
          })
        }
      });
      if (done) done();
    });
  }

  function openChoosePopup(triggerBtn, set) {
    var overlay = buildChooseModal();
    var grid = overlay.querySelector('#fm-choose-grid');
    var sub = overlay.querySelector('#fm-choose-sub');
    var consentCb = overlay.querySelector('#fm-choose-consent');
    var addBtn = overlay.querySelector('#fm-choose-add');
    sub.textContent = set.productTitle
      ? ('Select the ' + set.productTitle + ' colours you’d like — they’re on us.')
      : 'Select the colours you’d like — they’re on us.';

    function selectedIds() {
      return [].slice.call(grid.querySelectorAll('.fm-choose__tile[aria-pressed="true"]:not(.is-added)'))
        .map(function (t) { return t.getAttribute('data-vid'); });
    }
    function updateAdd() {
      var n = selectedIds().length;
      addBtn.disabled = !(n > 0 && consentCb.checked);
      addBtn.querySelector('span').textContent = n > 0 ? ('Add my sample' + (n > 1 ? 's' : '') + ' (' + n + ')') : 'Add my samples';
    }
    consentCb.onchange = updateAdd;

    /* Mark colours already in the cart as added (no double-add). */
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var inCart = {};
        (cart.items || []).forEach(function (it) { inCart[String(it.variant_id)] = true; });
        grid.innerHTML = '';
        set.variants.forEach(function (v) {
          var already = !!inCart[String(v.id)];
          var tile = document.createElement('button');
          tile.type = 'button';
          tile.className = 'fm-choose__tile' + (already ? ' is-added' : '');
          tile.setAttribute('data-vid', v.id);
          tile.setAttribute('aria-pressed', already ? 'true' : 'false');
          if (already) tile.disabled = true;
          tile.innerHTML =
            (v.image ? '<img src="' + v.image + '" alt="">' : '<span class="fm-choose__swatch"></span>') +
            '<span class="fm-choose__name"></span>' +
            '<span class="fm-choose__state">' + (already ? 'In cart' : '') + '</span>';
          tile.querySelector('.fm-choose__name').textContent = v.colour || '';
          if (!already) {
            tile.addEventListener('click', function () {
              var on = tile.getAttribute('aria-pressed') === 'true';
              tile.setAttribute('aria-pressed', on ? 'false' : 'true');
              tile.classList.toggle('is-selected', !on);
              updateAdd();
            });
          }
          grid.appendChild(tile);
        });
        consentCb.checked = !!getConsent();
        updateAdd();
      });

    addBtn.onclick = function () {
      var ids = selectedIds();
      if (!ids.length || !consentCb.checked) return;
      addBtn.disabled = true;
      chooseAdd(set, ids, function () { overlay.removeAttribute('data-open'); })
        .catch(function (e) { console.error('FM sample multi-add failed', e); addBtn.disabled = false; });
    };

    overlay.setAttribute('data-open', '');
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('.js-fm-sample-add');
    if (!btn) return;
    event.preventDefault();
    if (btn.disabled) return;
    /* If this product has a sample SET (custom.sample_product metafield),
       open the multi-colour chooser (consent built in). Otherwise fall back
       to the single Free Sample product flow (legacy / not-yet-migrated). */
    var handle = btn.getAttribute('data-product-handle') || '';
    var set = window.fmSampleSets && window.fmSampleSets[handle];
    if (set && set.variants && set.variants.length) {
      openChoosePopup(btn, set);
      return;
    }
    /* Gate every add behind consent. Once given this session, subsequent
       adds skip the modal (still stamped with the recorded consent). */
    if (getConsent()) {
      performAdd(btn);
    } else {
      openConsentModal(btn);
    }
  });
})();
